import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, Clock, Activity, Check, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Composant de popup d'alerte horaire de production
 * Affiche automatiquement une alerte pendant les heures de travail configurées
 * À utiliser sur les pages: CommandCenter, Conduite, Cibles
 */
const HourlyAlertPopup = ({ enabled = true }) => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkAlert = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await axios.get(`${API}/api/conduite/alerts/check-new`);
      if (res.data?.has_new_alert) {
        fetchAlertData();
      }
    } catch (error) {
      // Silently fail - alerts are optional
    }
  }, [enabled]);

  const fetchAlertData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/conduite/alerts/hourly-status`);
      if (res.data) {
        setAlertData(res.data);
        setShowAlert(true);
      }
    } catch (error) {
      console.error('Erreur chargement alerte:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissAlert = async () => {
    if (!alertData?.alert_id) return;
    try {
      await axios.post(`${API}/api/conduite/alerts/mark-seen?alert_id=${alertData.alert_id}`);
      setShowAlert(false);
      setAlertData(null);
    } catch (error) {
      console.error('Erreur dismiss alerte:', error);
      setShowAlert(false);
    }
  };

  useEffect(() => {
    if (enabled) {
      // Vérifier après 3 secondes
      const timer = setTimeout(checkAlert, 3000);
      // Puis vérifier toutes les 5 minutes
      const interval = setInterval(checkAlert, 5 * 60 * 1000);
      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [checkAlert, enabled]);

  if (!enabled) return null;

  return (
    <Dialog open={showAlert} onOpenChange={() => {/* Ne pas fermer sans valider */}}>
      <DialogContent className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-amber-500/50 max-w-2xl shadow-2xl shadow-amber-500/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 rounded-lg bg-amber-500/20 border border-amber-500/30">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-white">Point de Production Horaire</span>
            <span className="text-sm font-normal text-slate-400 ml-auto">
              {alertData?.hour !== undefined ? `${alertData.hour}h00` : ''}
            </span>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            État de la production éditoriale pour aujourd'hui
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
          </div>
        ) : alertData ? (
          <div className="py-3 space-y-3 max-h-[70vh] overflow-y-auto">
            {/* Résumé global - Compact en ligne */}
            <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-900/60 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">{alertData.summary?.total_sites || 0} sites</span>
                <span className="text-slate-600">|</span>
                <span className="text-xs"><span className="text-emerald-400 font-bold">{alertData.summary?.total_published || 0}</span> <span className="text-slate-500">pub.</span></span>
                <span className="text-xs"><span className="text-amber-400 font-bold">{alertData.summary?.total_draft || 0}</span> <span className="text-slate-500">brouil.</span></span>
                <span className="text-xs"><span className="text-red-400 font-bold">{Math.max(0, (alertData.summary?.total_objective || 0) - (alertData.summary?.total_published || 0) - (alertData.summary?.total_draft || 0))}</span> <span className="text-slate-500">restants</span></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">/{alertData.summary?.total_objective || 0}</span>
                <span className="text-sm font-bold text-sky-400">{alertData.summary?.completion_percentage || 0}%</span>
              </div>
            </div>

            {/* Barre de progression compacte - 3 segments */}
            {(() => {
              const published = alertData.summary?.total_published || 0;
              const draft = alertData.summary?.total_draft || 0;
              const objective = alertData.summary?.total_objective || 1;
              const unprepared = Math.max(0, objective - published - draft);
              const publishedPct = (published / objective) * 100;
              const draftPct = (draft / objective) * 100;
              const unpreparedPct = (unprepared / objective) * 100;
              return (
                <div className="h-5 bg-slate-800 rounded-md overflow-hidden flex">
                  {published > 0 && (
                    <div className="h-full bg-emerald-500 flex items-center justify-center" style={{ width: `${publishedPct}%` }}>
                      <span className="text-[10px] font-bold text-white">{published}</span>
                    </div>
                  )}
                  {draft > 0 && (
                    <div className="h-full bg-amber-500 flex items-center justify-center" style={{ width: `${draftPct}%` }}>
                      <span className="text-[10px] font-bold text-white">{draft}</span>
                    </div>
                  )}
                  {unprepared > 0 && (
                    <div className="h-full bg-red-500/80 flex items-center justify-center" style={{ width: `${unpreparedPct}%` }}>
                      <span className="text-[10px] font-bold text-white">{unprepared}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Sites en grille de carrés */}
            {(() => {
              const allSites = alertData.sites || [];
              const strictCompleted = allSites.filter(s => s.strict_objective && s.status === 'completed');
              const activeSites = allSites.filter(s => !(s.strict_objective && s.status === 'completed'));
              
              const categoryOrder = { 'va': 0, 'groupe': 1, 'autre': 2 };
              const sortedActive = [...activeSites].sort((a, b) => {
                const catA = categoryOrder[a.category?.toLowerCase()] ?? 2;
                const catB = categoryOrder[b.category?.toLowerCase()] ?? 2;
                if (catA !== catB) return catA - catB;
                const statusOrder = { 'late': 0, 'behind': 1, 'on_track': 2, 'completed': 3 };
                return (statusOrder[a.status] || 2) - (statusOrder[b.status] || 2);
              });

              const SiteCard = ({ site }) => {
                const statusColors = {
                  'completed': 'bg-emerald-500/20 border-emerald-500/50',
                  'late': 'bg-red-500/20 border-red-500/50',
                  'behind': 'bg-amber-500/20 border-amber-500/50',
                  'on_track': 'bg-slate-800/80 border-slate-600/50'
                };
                const pctPublished = (site.published / site.daily_objective) * 100;
                const pctDraft = (site.draft / site.daily_objective) * 100;
                const pctRemaining = Math.max(0, 100 - pctPublished - pctDraft);
                
                return (
                  <div 
                    className={`relative p-2.5 rounded-lg border transition-all hover:scale-[1.02] ${statusColors[site.status] || statusColors.on_track}`}
                    title={`${site.name} - ${site.status_label}`}
                  >
                    <div className="text-[11px] font-semibold text-slate-200 mb-2 leading-tight">{site.name}</div>
                    <div className="h-2 bg-slate-700/30 rounded-full overflow-hidden flex mb-1.5">
                      {site.published > 0 && (
                        <div className="h-full bg-emerald-500" style={{ width: `${pctPublished}%` }} />
                      )}
                      {site.draft > 0 && (
                        <div className="h-full bg-amber-500" style={{ width: `${pctDraft}%` }} />
                      )}
                      {pctRemaining > 0 && (
                        <div className="h-full bg-red-500/80" style={{ width: `${pctRemaining}%` }} />
                      )}
                    </div>
                    <div className="flex items-center justify-between text-[9px]">
                      <div className="flex items-center gap-1.5">
                        {site.published > 0 && (
                          <span className="text-emerald-400 font-bold">{site.published} pub</span>
                        )}
                        {site.draft > 0 && (
                          <span className="text-amber-400 font-bold">{site.draft} brouil</span>
                        )}
                        {site.published === 0 && site.draft === 0 && (
                          <span className="text-slate-500">0 article</span>
                        )}
                      </div>
                      <span className="text-slate-400">obj: {site.daily_objective}</span>
                    </div>
                    {(site.status === 'late' || site.status === 'behind') && (
                      <div className={`text-[8px] text-center mt-1.5 py-0.5 rounded ${
                        site.status === 'late' ? 'bg-red-500/30 text-red-300' : 'bg-amber-500/30 text-amber-300'
                      }`}>
                        {site.status === 'late' ? '⚠ En retard' : '● À accélérer'}
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div className="space-y-3">
                  {sortedActive.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-3 h-3 text-slate-400" />
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider">En cours ({sortedActive.length})</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        {sortedActive.map((site, idx) => (
                          <SiteCard key={site.id || idx} site={site} />
                        ))}
                      </div>
                    </div>
                  )}

                  {strictCompleted.length > 0 && (
                    <div className="pt-2 border-t border-slate-700/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Check className="w-3 h-3 text-emerald-400" />
                        <span className="text-[10px] text-emerald-400/80 uppercase tracking-wider">Complétés ({strictCompleted.length})</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 opacity-70">
                        {strictCompleted.map((site, idx) => (
                          <SiteCard key={site.id || idx} site={site} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Indicateurs de warning compacts */}
            {(alertData.summary?.sites_late > 0 || alertData.summary?.sites_behind > 0) && (
              <div className="flex items-center gap-2 px-2 py-1.5 bg-amber-950/30 border border-amber-500/30 rounded-lg">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                <span className="text-[10px] text-amber-300">
                  {alertData.summary?.sites_late > 0 && `${alertData.summary.sites_late} en retard`}
                  {alertData.summary?.sites_late > 0 && alertData.summary?.sites_behind > 0 && ' • '}
                  {alertData.summary?.sites_behind > 0 && `${alertData.summary.sites_behind} à accélérer`}
                </span>
              </div>
            )}

            {/* Légende statuts */}
            <div className="flex items-center justify-center gap-4 text-[9px] text-slate-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-emerald-500"></span>Publié</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-amber-500"></span>Brouillon</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-red-500"></span>Restant</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            Aucune donnée disponible
          </div>
        )}

        <DialogFooter>
          <Button 
            onClick={dismissAlert}
            className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white font-semibold"
          >
            <Check className="w-4 h-4 mr-2" />
            J'ai pris connaissance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HourlyAlertPopup;
